import { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useDebouncedCallback } from "use-debounce";

import Button from "../Buttons/Button";
import {
  DeleteIcon,
  PrintIcon,
  ShareIcon,
} from "../../../assets/icons/iconsProvider";
import {
  buttonNameConstants,
  buttonTypeConstants,
  EMPTY_STRING,
  storeConstants,
  systemConstants,
  TABLE_COLUMNS,
} from "../../../constants";
import AgGridPaginationComponent from "./AgGridPaginationComponent";
import AGGridToolTip from "../toolTipComponent/AGGridToolTip";
import CustomGridHeader from "./CustomGridHeader";
import { excludeNullColumns, isTransferInventoryEnabled } from "../../utility";
import { userActions } from "../../../redux/actions";
import { connect } from "react-redux";

const ServerPaginationTable = ({
  user,
  colDefs,
  tableId,
  gridRef,
  gridData,
  gridState,
  getRowStyle,
  tableHeight,
  selectedRows,
  stores = [],
  onCellClicked,
  updateGridState,
  components = null,
  rowClassRules = {},
  defaultColDef = {},
  pagination = true,
  onSelectionChanged,
  animateRows = true,
  getRowId = () => {},
  editType = "fullRow",
  updateUserPreferences,
  tooltipShowDelay = 0,
  handleDeleteAllClick,
  handlePrintLabelClick,
  isDataLoading = false,
  setIsInlineEditRefresh,
  singleClickEdit = true,
  handleTransferInventory,
  tooltipHideDelay = 4000,
  tooltipInteraction = true,
  suppressCellFocus = true,
  currentStoreCompany = {},
  rowSelection = "multiple",
  scrollRef = { current: 0 },
  isDisablePaginationActions,
  isInlineEditRefresh = false,
  alwaysShowVerticalScroll = false,
  suppressRowClickSelection = true,
  inlineEditRefreshScrollValue = 0,
  suppressRowHoverHighlight = true,
  alwaysShowHorizontalScroll = false,
  suppressDragLeaveHidesColumns = true,
  isSaveColumnSizingInUserPeferences = true,
  defaultGridSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
}) => {
  const previousGridWidth = useRef(null);
  var fromEdit = false;
  var fromEditScrollValue = 0;
  const savedColumnSizing =
    user?.userPreferences?.tables?.[tableId]?.columnSizing;
  const lastColumnKey =
    colDefs[colDefs.length - 1]?.field ?? TABLE_COLUMNS.ACTION_ID;

  const onFirstDataRendered = useCallback(
    (params) => {
      params.api.gridBodyCtrl.eBodyViewport.scrollTop = scrollRef.current;
      // eslint-disable-next-line
      fromEdit = true;
      // eslint-disable-next-line
      fromEditScrollValue = scrollRef.current;
    },
    [savedColumnSizing]
  );
  const onModelUpdated = useCallback(
    (params) => {
      if (fromEdit) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          fromEditScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop = fromEditScrollValue;
          // eslint-disable-next-line
          fromEdit = false;
          // eslint-disable-next-line
          fromEditScrollValue = 0;
        }
      } else if (isInlineEditRefresh) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          inlineEditRefreshScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop =
            inlineEditRefreshScrollValue;
          setIsInlineEditRefresh?.(false);
        }
      }
    },
    [isInlineEditRefresh]
  );
  const onSortChanged = () => {
    scrollRef.current = 0;
    gridRef.current.api.gridBodyCtrl.eBodyViewport.scrollTop = 0;
  };

  const agGridDefaultColDef = useMemo(() => {
    return {
      cellStyle: { display: "flex", alignItems: "center" },
      resizable: true,
      lockPosition: false,
      tooltipComponent: AGGridToolTip,
      headerTooltipComponent: AGGridToolTip,
      colResizeDefault: "shift",
      headerComponentParams: {
        gridState: gridState,
        updateGridState: updateGridState,
      },
      rowSelection: "single",
      suppressRowClickSelection: false,
      minWidth: storeConstants.AG_GRID_MIN_COLUMN_WIDTH,
      maxWidth: undefined,
      ...defaultColDef,
    };
    // eslint-disable-next-line
  }, [gridState]);

  // apply over the top validation on column definations as per client requirement like selection checkbox must always stays at the start, action column is not resizable, stick to the right like it is pinned to right, it resize automatically when resizing other columns like network request dev tools column resizing
  const getFormattedColDefs = () => {
    let filteredColDefs = excludeNullColumns(colDefs);
    return filteredColDefs.map((col) => {
      let formattedCol = {
        ...col,
      };
      // first column with checkbox selection must be locked
      if (col.checkboxSelection) {
        formattedCol.lockPosition = true;
      }
      // last column must stick to last and act similar to dev tools network tab column resizing if resize columns is true
      else if (col.field === lastColumnKey && agGridDefaultColDef.resizable) {
        const isActionCol = col.field === TABLE_COLUMNS.ACTION_ID;
        formattedCol = {
          ...formattedCol,
          minWidth: 120, // prevent shrinking too much
          width: isActionCol ? 120 : undefined,
          maxWidth: undefined,
          suppressSizeToFit: isActionCol,
          flex: 1,
          sortable: false,
          resizable: false,
          lockPosition: "right",
          filter: false,
          cellStyle: {
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            overflow: "visible",
            border: "none",
          },
        };
      }
      return formattedCol;
    });
  };

  const agGridComponents = useMemo(() => {
    return {
      agColumnHeader: CustomGridHeader,
    };
  }, []);

  const onGridReady = (params) => {
    applyColumnSizing(params);
  };

  const onGridSizeChanged = useCallback(
    (params) => {
      var gridWidth = document.querySelector(".ag-body-viewport").clientWidth;
      if (
        previousGridWidth.current !== null &&
        gridWidth <= previousGridWidth.current
      )
        return;
      previousGridWidth.current = gridWidth;
      var columnsToShow = [];
      var columnsToHide = [];
      var totalColsWidth = 0;
      var allColumns = params.api.getColumns();
      if (allColumns && allColumns.length > 0) {
        for (const column of allColumns) {
          totalColsWidth += column.getMinWidth() || 0;
          if (totalColsWidth > gridWidth) {
            columnsToHide.push(column.getColId());
          } else {
            columnsToShow.push(column.getColId());
          }
        }
      }
      params.api.setColumnsVisible(columnsToShow, true);
      params.api.setColumnsVisible(columnsToHide, false);
      applyColumnSizing(params); //user close/open sidebar, resize screen (switch between displays, zoom in, zoom out etc)
    },
    // eslint-disable-next-line
    [window, savedColumnSizing]
  );

  const handleUpdateUserPreferences = useDebouncedCallback(
    (updatedColumnSizing) => {
      if (isSaveColumnSizingInUserPeferences) {
        updateUserPreferences(
          user,
          {},
          { tableId, columnSizing: updatedColumnSizing }
        );
      }
    },
    systemConstants.USER_PREFERENCES_DEBOUNCE
  );

  const getRowClass = () => {
    let rowClass = EMPTY_STRING;
    // if inline edit is present on table then add this class and it will add a border when user clicks the row and row becomes editable
    if (singleClickEdit) {
      rowClass = rowClass + "enable-row-focus-border";
    }
    return rowClass;
  };

  // Dynamically set the maxWidth of column to make sure horizantal scroll never shows up. During the column resize if the sum of width of all columns exceeds the table width means horizantal scroll is visible so we set the max width of column being resized with it's current width so that it's width cannot increase any further. And if no horizantal scroll & there is a column with width not equals to the max width means we have updated it's maxWidth so we must reset all columns maxWidth so that all column are resizeable again (as when scroll was visible we set the maxWidth of the column and now as there is no scrool so we must reset the max width so all columns are resizeable again and as soon as scroll shows up the cycle repeats)
  const onColumnResized = (params) => {
    if (params.source !== "uiColumnResized" || !params.column) return;

    const api = params.api;
    const columnState = api.getColumnState();

    const agBodyViewport = api.gridBodyCtrl?.eBodyViewport;
    const totalWidthOfTable = agBodyViewport?.clientWidth ?? 0;

    if (!totalWidthOfTable) return;

    const otherColumnsWidth = columnState
      .filter((col) => !col.hide && col.colId !== params.column.colId)
      .reduce((sum, col) => sum + (col.width ?? 0), 0);

    const maxAllowedWidth = totalWidthOfTable - otherColumnsWidth;

    if (maxAllowedWidth > 0) {
      // Set maxWidth on the column def
      const allColDefs = api.getColumnDefs();
      const updatedColDefs = allColDefs.map((colDef) => {
        if (
          colDef.field === params.column.colId ||
          colDef.colId === params.column.colId
        ) {
          return { ...colDef, maxWidth: maxAllowedWidth };
        }
        return colDef;
      });
      api.setColumnDefs(updatedColDefs);
    }

    // Save preferences only when user finishes dragging
    if (params.finished) {
      // Reset maxWidth on all columns after drag ends so they stay resizable later
      const allColDefs = api.getColumnDefs();
      const resetColDefs = allColDefs.map((colDef) => ({
        ...colDef,
        maxWidth:
          colDef.field === TABLE_COLUMNS.ACTION_ID
            ? undefined
            : storeConstants.AG_GRID_MAX_COLUMN_WIDTH,
      }));
      api.setColumnDefs(resetColDefs);

      const newColumnSizing = {};
      api.getColumnState().forEach((col) => {
        newColumnSizing[col.colId] = col.width;
      });
      handleUpdateUserPreferences(newColumnSizing);
    }
  };

  const applyColumnSizing = (params) => {
    const api = gridRef.current.api || params.api;
    if (!api) return;
    const allColumns = api.getColumns && api.getColumns();
    if (!allColumns || allColumns.length === 0) return;

    const columnApi = gridRef?.current?.columnApi;

    // if saved column sizing exists then apply that one otherwise set column to fit the grid available space
    if (savedColumnSizing && columnApi) {
      const columnState = Object.entries(savedColumnSizing).map(
        ([colId, width]) => ({ colId, width })
      );

      columnApi?.applyColumnState({ state: columnState });
    } else {
      params.api.sizeColumnsToFit();
    }
  };

  return (
    <div
      className="ag-theme-quartz" // applying the grid theme
      style={{
        height: tableHeight,
        paddingBottom: "5px",
        width: "100%",
        maxWidth: "100%",
      }} // the grid will fill the size of the parent container
    >
      {selectedRows?.length > 0 && (
        <div
          className={`d-flex justify-content-end gap-2 ${
            handlePrintLabelClick ? "py-2" : ""
          } `}
        >
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.DELETE}
              buttonType={buttonTypeConstants.RED_BUTTON}
              IconImage={DeleteIcon}
              type="button"
              handleClick={() => handleDeleteAllClick()}
            />
          )}
          {isTransferInventoryEnabled(currentStoreCompany, stores) && (
            <Button
              label={buttonNameConstants.TRANSFER}
              buttonType={buttonTypeConstants.GHOST_BUTTON}
              IconImage={ShareIcon}
              type="button"
              handleClick={() => handleTransferInventory()}
            />
          )}
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.PRINT_LABEL}
              type="button"
              IconImage={PrintIcon}
              buttonType={buttonTypeConstants.LIGHT_PURPLE}
              handleClick={() => handlePrintLabelClick()}
            />
          )}
        </div>
      )}
      <AgGridReact
        ref={gridRef}
        rowData={gridData}
        getRowId={getRowId}
        editType={editType}
        columnDefs={getFormattedColDefs()}
        onGridReady={onGridReady}
        animateRows={animateRows}
        getRowStyle={getRowStyle}
        getRowClass={getRowClass}
        rowSelection={rowSelection}
        rowClassRules={rowClassRules}
        onCellClicked={onCellClicked}
        suppressHorizontalScroll={true}
        singleClickEdit={singleClickEdit}
        tooltipShowDelay={tooltipShowDelay}
        tooltipShowMode={"whenTruncated"}
        tooltipHideDelay={tooltipHideDelay}
        suppressCellFocus={suppressCellFocus}
        onGridSizeChanged={onGridSizeChanged}
        onSelectionChanged={onSelectionChanged}
        tooltipInteraction={tooltipInteraction}
        onFirstDataRendered={onFirstDataRendered}
        autoSizeStrategy={defaultGridSizeStrategy}
        components={components || agGridComponents}
        alwaysShowVerticalScroll={alwaysShowVerticalScroll}
        defaultColDef={agGridDefaultColDef}
        suppressRowClickSelection={suppressRowClickSelection}
        suppressRowHoverHighlight={suppressRowHoverHighlight}
        alwaysShowHorizontalScroll={alwaysShowHorizontalScroll}
        suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
        onBodyScroll={(event) => {
          scrollRef.current = event.top;
        }}
        onModelUpdated={onModelUpdated}
        onSortChanged={onSortChanged}
        onColumnResized={onColumnResized}
      />
      {pagination && (
        <AgGridPaginationComponent
          gridData={gridData}
          gridState={gridState}
          isDataLoading={isDataLoading}
          updateGridState={updateGridState}
          isDisablePaginationActions={isDisablePaginationActions}
          gridRef={gridRef}
          scrollRef={scrollRef}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  store: state.store,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  updateUserPreferences: (user, updatedUserPeferences, updatedColumnSizing) =>
    dispatch(
      userActions.updateUserPreferences(
        user,
        updatedUserPeferences,
        updatedColumnSizing
      )
    ),
});

//-------Export Login Component
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ServerPaginationTable);
